import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';

const AuthContext = createContext(null);

const API = process.env.REACT_APP_BACKEND_URL;

// Helper to format API error details
function formatApiErrorDetail(detail) {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).filter(Boolean).join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null); // null = checking, false = not authenticated, object = authenticated
  const [loading, setLoading] = useState(true);
  // "View as customer" toggle — platform admins can hide their internal admin
  // affordances (Waitlist, etc.) to see exactly what a regular customer sees.
  // Persisted in localStorage so it survives reloads but is cleared on logout.
  const [viewAsCustomer, setViewAsCustomerState] = useState(() => {
    try {
      return localStorage.getItem('pv_view_as_customer') === '1';
    } catch {
      return false;
    }
  });

  const setViewAsCustomer = useCallback((next) => {
    setViewAsCustomerState(next);
    try {
      if (next) localStorage.setItem('pv_view_as_customer', '1');
      else localStorage.removeItem('pv_view_as_customer');
    } catch (err) {
      // localStorage blocked (private mode / disabled cookies) — the
      // customer-view toggle simply won't persist across reloads.
      console.warn('AuthContext: unable to persist view-as-customer flag:', err);
    }
  }, []);

  const toggleViewAsCustomer = useCallback(() => {
    setViewAsCustomer(!viewAsCustomer);
  }, [viewAsCustomer, setViewAsCustomer]);

  const checkAuth = useCallback(async () => {
    // CRITICAL: If returning from OAuth callback, skip the /me check.
    // AuthCallback will exchange the session_id and establish the session first.
    if (window.location.hash?.includes('session_id=')) {
      setLoading(false);
      return;
    }
    
    try {
      const response = await axios.get(`${API}/api/auth/me`, {
        withCredentials: true
      });
      setUser(response.data);
    } catch (error) {
      setUser(false);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  const login = async (email, password) => {
    try {
      const response = await axios.post(
        `${API}/api/auth/login`,
        { email, password },
        { withCredentials: true }
      );
      setUser(response.data);
      return { success: true, user: response.data };
    } catch (error) {
      const message = formatApiErrorDetail(error.response?.data?.detail) || error.message;
      return { success: false, error: message };
    }
  };

  const register = async (email, password, name, role = 'cleaner') => {
    try {
      const response = await axios.post(
        `${API}/api/auth/register`,
        { email, password, name, role },
        { withCredentials: true }
      );
      setUser(response.data);
      return { success: true, user: response.data };
    } catch (error) {
      const message = formatApiErrorDetail(error.response?.data?.detail) || error.message;
      return { success: false, error: message };
    }
  };

  const logout = async () => {
    try {
      await axios.post(`${API}/api/auth/logout`, {}, { withCredentials: true });
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      setUser(false);
      // Clear customer-view mode on logout so the next user starts fresh.
      setViewAsCustomer(false);
    }
  };

  const loginWithGoogle = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + '/auth/callback';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const handleGoogleCallback = async (sessionId) => {
    try {
      const response = await axios.post(
        `${API}/api/auth/google/callback`,
        { session_id: sessionId },
        { withCredentials: true }
      );
      setUser(response.data);
      return { success: true, user: response.data };
    } catch (error) {
      const message = formatApiErrorDetail(error.response?.data?.detail) || error.message;
      return { success: false, error: message };
    }
  };

  const refreshUser = async () => {
    try {
      const response = await axios.get(`${API}/api/auth/me`, {
        withCredentials: true
      });
      setUser(response.data);
    } catch (error) {
      setUser(false);
    }
  };

  // Check if user has required role
  const hasRole = (allowedRoles) => {
    if (!user) return false;
    return allowedRoles.includes(user.role);
  };

  // Check if user can perform action
  const canManageUsers = () => hasRole(['property_manager']);
  const canViewAll = () => hasRole(['property_manager', 'owner']);
  const canEditTask = (task) => {
    if (hasRole(['property_manager', 'owner'])) return true;
    return task?.assigned_worker_id === user?.id;
  };

  // True platform admin flag (real, unaffected by view-as-customer)
  const isTruePlatformAdmin = !!user && user !== false && user.is_platform_admin === true;
  // Effective platform admin used for UI gating — flipped off when admin is
  // viewing as a customer.
  const isPlatformAdmin = isTruePlatformAdmin && !viewAsCustomer;

  const value = {
    user,
    loading,
    login,
    register,
    logout,
    loginWithGoogle,
    handleGoogleCallback,
    refreshUser,
    hasRole,
    canManageUsers,
    canViewAll,
    canEditTask,
    isAuthenticated: !!user && user !== false,
    // Platform admin / customer-view
    isTruePlatformAdmin,
    isPlatformAdmin,
    viewAsCustomer,
    toggleViewAsCustomer,
    setViewAsCustomer,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export default AuthContext;
